import { useEffect, useState } from "react";
import { useStateValue } from "../context/state/provider";
import { makeStyles } from "@material-ui/core/styles";
import Switch from "@material-ui/core/Switch";
import {
	resetAccessibilitySettings,
	resizeGlobalFonts,
	updateVisibilitySetting,
	getA11yPrefs,
	setA11yPref,
	DEFAULT_A11Y_PREFS,
} from "../helpers/accessibilityHelpers";
import { FONT_SIZE } from "../constants/fontSize.constants";
import { VISIBILITY } from "../constants/visibility.constants";

const useStyles = makeStyles({
	panel: {
		position: "absolute",
		overflowY: "auto",
		overflowX: "hidden",
		top: 0,
		bottom: 0,
		left: 0,
		right: 0,
		animation: "fadeIn 0.25s ease",
		padding: "16px 18px 28px",
		fontFamily: "var(--font-sans)",
		background: "linear-gradient(180deg, #fafbfc 0%, #fff 35%)",
	},
	intro: {
		margin: "0 0 18px",
		fontSize: "13.5px",
		lineHeight: 1.45,
		color: "var(--color-muted)",
	},
	section: {
		marginBottom: 18,
		padding: "14px 14px 10px",
		borderRadius: 14,
		border: "1px solid var(--surface-border)",
		background: "#fff",
		boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
	},
	sectionTitle: {
		margin: "0 0 10px",
		fontSize: "13px",
		fontWeight: 700,
		letterSpacing: "-0.01em",
		color: "var(--color-dark-grey)",
		textTransform: "uppercase",
	},
	chipRow: {
		display: "flex",
		flexWrap: "wrap",
		gap: 8,
	},
	chip: {
		border: "1px solid var(--surface-border)",
		background: "var(--color-alt-grey)",
		color: "var(--color-dark-grey)",
		fontFamily: "var(--font-sans)",
		fontSize: 13,
		fontWeight: 600,
		padding: "8px 12px",
		borderRadius: 10,
		cursor: "pointer",
		lineHeight: 1.2,
		transition: "background 0.15s ease, border-color 0.15s ease, color 0.15s ease",
		"&:hover": {
			borderColor: "var(--active-brand-color)",
		},
		"&:focus-visible": {
			outline: "2px solid var(--active-brand-color)",
			outlineOffset: 2,
		},
	},
	chipSelected: {
		background: "var(--active-brand-color)",
		borderColor: "var(--active-brand-color)",
		color: "#fff",
	},
	toggleRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: 12,
		padding: "10px 0",
		borderTop: "1px solid var(--surface-border)",
		"&:first-of-type": {
			borderTop: "none",
			paddingTop: 2,
		},
	},
	toggleCopy: {
		minWidth: 0,
		textAlign: "left",
	},
	toggleLabel: {
		margin: 0,
		fontSize: 14,
		fontWeight: 600,
		color: "var(--color-dark-grey)",
		letterSpacing: "-0.01em",
	},
	toggleHint: {
		margin: "2px 0 0",
		fontSize: 12,
		color: "var(--color-muted)",
		lineHeight: 1.35,
	},
	resetButton: {
		width: "100%",
		marginTop: 6,
		padding: "11px 14px",
		borderRadius: 12,
		border: "1px solid var(--surface-border)",
		background: "#fff",
		color: "var(--color-dark-grey)",
		fontFamily: "var(--font-sans)",
		fontSize: 14,
		fontWeight: 600,
		cursor: "pointer",
		"&:hover": {
			background: "var(--color-alt-grey)",
		},
		"&:disabled": {
			opacity: 0.45,
			cursor: "default",
		},
	},
});

const FONT_OPTIONS = [
	{ id: FONT_SIZE.SMALL, label: "Small" },
	{ id: FONT_SIZE.STANDARD, label: "Default" },
	{ id: FONT_SIZE.LARGE, label: "Large" },
	{ id: FONT_SIZE.EXTRA_LARGE, label: "Extra large" },
];

const VISIBILITY_OPTIONS = [
	{ id: VISIBILITY.STANDARD, label: "Standard" },
	{ id: VISIBILITY.HIGH_CONTRAST, label: "High contrast" },
	{ id: VISIBILITY.MONOCHROME, label: "Monochrome" },
];

const TOGGLE_OPTIONS = [
	{
		key: "readableFont",
		label: "Readable font",
		hint: "Atkinson Hyperlegible — clearer letter shapes",
	},
	{
		key: "spaciousText",
		label: "Comfortable spacing",
		hint: "More line height and letter spacing",
	},
	{
		key: "underlineLinks",
		label: "Underline links",
		hint: "Make links easier to spot",
	},
	{
		key: "reduceMotion",
		label: "Reduce motion",
		hint: "Turn off animations and transitions",
	},
	{
		key: "largeTargets",
		label: "Larger tap targets",
		hint: "Bigger buttons for touch and motor ease",
	},
	{
		key: "strongFocus",
		label: "Strong focus rings",
		hint: "High-visibility keyboard focus outlines",
	},
];

export default function AccessibilityPanel() {
	const classes = useStyles();
	const [{ colorSettings, clientId, visibility, fontSize }, dispatch] =
		useStateValue();
	const [prefs, setPrefs] = useState(DEFAULT_A11Y_PREFS);

	useEffect(() => {
		setPrefs(getA11yPrefs());
	}, []);

	const setFont = (size) => {
		dispatch({ type: "SET_FONT_SIZE", fontSize: size });
		localStorage.setItem("fontSizeSetting", size);
		resizeGlobalFonts(size);
	};

	const setVisibility = (next) => {
		dispatch({ type: "SET_VISIBILITY", visibility: next });
		localStorage.setItem("visibilitySetting", next);
		updateVisibilitySetting(next, colorSettings);
	};

	const togglePref = (key) => {
		const next = setA11yPref(key, !prefs[key]);
		setPrefs(next);
	};

	const isDefault =
		visibility === VISIBILITY.STANDARD &&
		fontSize === FONT_SIZE.STANDARD &&
		TOGGLE_OPTIONS.every((opt) => !prefs[opt.key]);

	const handleSettingsReset = () => {
		dispatch({ type: "SET_VISIBILITY", visibility: VISIBILITY.STANDARD });
		dispatch({ type: "SET_FONT_SIZE", fontSize: FONT_SIZE.STANDARD });
		localStorage.setItem("visibilitySetting", VISIBILITY.STANDARD);
		localStorage.setItem("fontSizeSetting", FONT_SIZE.STANDARD);
		resetAccessibilitySettings(colorSettings, clientId);
		setPrefs({ ...DEFAULT_A11Y_PREFS });
	};

	return (
		<div className={classes.panel} role="region" aria-label="Accessibility settings">
			<p className={classes.intro}>
				Adjust display settings for this browser. Changes save automatically and apply immediately.
			</p>

			<section className={classes.section} aria-labelledby="a11y-font-heading">
				<h2 id="a11y-font-heading" className={classes.sectionTitle}>
					Text size
				</h2>
				<div className={classes.chipRow} role="group" aria-label="Text size options">
					{FONT_OPTIONS.map((opt) => (
						<button
							key={opt.id}
							type="button"
							className={`${classes.chip} ${fontSize === opt.id ? classes.chipSelected : ""}`}
							aria-pressed={fontSize === opt.id}
							onClick={() => setFont(opt.id)}
						>
							{opt.label}
						</button>
					))}
				</div>
			</section>

			<section className={classes.section} aria-labelledby="a11y-contrast-heading">
				<h2 id="a11y-contrast-heading" className={classes.sectionTitle}>
					Color & contrast
				</h2>
				<div className={classes.chipRow} role="group" aria-label="Contrast options">
					{VISIBILITY_OPTIONS.map((opt) => (
						<button
							key={opt.id}
							type="button"
							className={`${classes.chip} ${visibility === opt.id ? classes.chipSelected : ""}`}
							aria-pressed={visibility === opt.id}
							onClick={() => setVisibility(opt.id)}
						>
							{opt.label}
						</button>
					))}
				</div>
			</section>

			<section className={classes.section} aria-labelledby="a11y-display-heading">
				<h2 id="a11y-display-heading" className={classes.sectionTitle}>
					Reading & interaction
				</h2>
				{TOGGLE_OPTIONS.map((opt) => (
					<div className={classes.toggleRow} key={opt.key}>
						<div className={classes.toggleCopy}>
							<p className={classes.toggleLabel} id={`a11y-${opt.key}-label`}>
								{opt.label}
							</p>
							<p className={classes.toggleHint}>{opt.hint}</p>
						</div>
						<Switch
							checked={Boolean(prefs[opt.key])}
							onChange={() => togglePref(opt.key)}
							color="primary"
							inputProps={{
								"aria-labelledby": `a11y-${opt.key}-label`,
							}}
						/>
					</div>
				))}
			</section>

			<button
				type="button"
				className={classes.resetButton}
				onClick={handleSettingsReset}
				disabled={isDefault}
			>
				Reset all accessibility settings
			</button>
		</div>
	);
}
